'use client';
import React, { useEffect, useMemo, useState } from 'react';
import { useRouter, useSearchParams, usePathname } from 'next/navigation';
import { useCurrentLocale, useScopedI18n } from '@locales/client';
import { loadDriver } from '@/shared/libs/lazy-driver';

import { useUser } from '@/shared/providers/user-provider/use-user';
import {
  useBonusWager,
  useBonusStartWager,
} from '@/shared/providers/notifications-provider/store';

import { brandIsPh } from '@/shared/config/brand-by-target.mjs';
import { getGamePlayPath } from '@/shared/libs/get-game-play-path';
import { scrollToTop } from '@/shared/libs/scroll-to-top';
import { useVisibleState } from '@/shared/hooks/useVisibleState';
import { useLocaleFormat } from '@/shared/hooks/useLocaleFormat';

import { useBonuses } from '@/entities/bonus/model/use-bonuses';
import { isDepositBonus } from '@/entities/bonus/model/is-deposit-bonus';
import { getGamesByIds } from '@/entities/game/model/api';
import { useConfirmDataPrompt } from '@/entities/data-confirmation/model/use-confirm-data-prompt';

import { BonusWrapper } from './ui/styles';
import { BonusSkeleton } from './ui';
import { BonusViewModal, BonusBurnModal } from './ui/modals';
import { PageHeaderSection } from './ui/page-header-section';
import { BonusMainContent } from './ui/bonus-main-content';
import { MultiAccountWarningModal } from '@/features/multi-account-warning-modal';
import { isBonusActive } from './model/utils';
import type { GameIdInput } from '@/entities/game/model/games-by-ids-logic';
import type { BonusOption } from './ui/modals/bonus-view/components/options-block';
import type { BonusOptionCondition } from './ui/modals/bonus-view/components/condition-block';

export interface BonusListItem {
  id: number;
  userBonusId?: string;
  category?: string;
  state?: string;
  games?: GameIdInput[] | null;
  name?: string;
  pic?: string;
  description: string;
  provider?: string | null;
  options?: (BonusOption & { conditions?: BonusOptionCondition[] })[];
}

const FILTERS: { category: string; key: string }[] = [
  { category: 'all', key: 'all' },
  { category: 'welcome', key: 'welcome' },
  { category: 'daily', key: 'every_days' },
  { category: 'regular', key: 'regular' },
  { category: 'personal', key: 'personal' },
  { category: 'wheel', key: 'wheel' },
];

const Bonuses = () => {
  const {
    data: bonusData,
    isLoading,
    activate,
    mutateBonuses,
    deactivate,
  } = useBonuses();
  const locale = useCurrentLocale();
  const { removeQueryParam } = useConfirmDataPrompt();
  const bonusWager = useBonusWager();
  const bonusStartWager = useBonusStartWager();

  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const formatUrl = useLocaleFormat();
  const isProfileBonusesPage = pathname.includes('/bonuses/info');

  const t = useScopedI18n('bonus');

  const { user, setUser } = useUser();
  const {
    visible: bonusBurnModalVisible,
    open: onBonusBurnModalOpen,
    close: onBonusBurnModalClose,
  } = useVisibleState(false);
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [currentBonus, setCurrentBonus] = useState<BonusListItem | null>(null);
  const [currentBonusBurn, setCurrentBonusBurn] =
    useState<BonusListItem | null>(null);
  const [progressWager, setProgressWager] = useState(0);

  const dataWelcomeAvailable = useMemo(
    () =>
      bonusData?.find(
        (el: BonusListItem | null) =>
          el?.category === 'welcome' && el?.state === 'available'
      ) || {},
    [bonusData]
  );

  const visibleFilters = useMemo(() => {
    const bonusCategories = new Set(
      bonusData?.map((bonus: BonusListItem) => bonus.category)
    );
    return FILTERS.filter(
      (value, index) => index === 0 || bonusCategories.has(value.category)
    );
  }, [bonusData]);

  const data = useMemo(() => {
    if (!bonusData) return bonusData;

    let result = [...bonusData];

    if (selectedTag) {
      result = result.filter((item) => item.category === selectedTag);
    }
    return result.sort((a, b) => {
      const aActive = isBonusActive(a);
      const bActive = isBonusActive(b);

      if (aActive || bActive) {
        if (aActive && !bActive) return -1;
        if (!aActive && bActive) return 1;
        return 0;
      }

      const aNeedsDeposits = a.state === 'need_deposits';
      const bNeedsDeposits = b.state === 'need_deposits';

      if (!aNeedsDeposits && bNeedsDeposits) {
        return -1;
      } else if (aNeedsDeposits && !bNeedsDeposits) {
        return 1;
      }

      if (a.timer < b.timer) {
        return -1;
      } else if (a.timer > b.timer) {
        return 1;
      } else {
        return 0;
      }
    });
  }, [bonusData, selectedTag]);

  useEffect(() => {
    if (bonusWager && bonusStartWager) {
      const currentProgressWager = (bonusWager / bonusStartWager) * 100;
      setProgressWager(currentProgressWager);
    }
  }, [bonusWager, bonusStartWager]);

  useEffect(() => {
    if (searchParams.has('bonusID') && !isLoading) {
      const currentBonus = bonusData?.find(
        (bonus: BonusListItem | null) =>
          bonus?.id === Number(searchParams.get('bonusID'))
      );
      setCurrentBonus(currentBonus);
      removeQueryParam('bonusID');
    }
    if (searchParams.has('promo') && !isLoading) {
      loadDriver().then((driver) => {
        const driverObj = driver({
          popoverClass: 'my-custom-popover-class',
        });
        driverObj.highlight({
          element: '#promo-form',
          popover: {
            title: t('onboarding.title'),
            description: t('onboarding.desc'),
          },
        });
      });
    }
  }, [searchParams, isLoading, bonusData]);

  useEffect(() => {
    if (
      isProfileBonusesPage &&
      bonusData?.length > 0 &&
      !dataWelcomeAvailable?.id
    ) {
      router.push('/bonuses');
    }
  }, [
    bonusData?.length,
    dataWelcomeAvailable?.id,
    isProfileBonusesPage,
    router,
  ]);

  const burnOpenClickHandler = (bonus: BonusListItem) => {
    setCurrentBonusBurn(bonus);
    onBonusBurnModalOpen();
  };

  const onActivate = async (
    bonus: BonusListItem,
    conditionActive: number | null,
    stateKey = null
  ) => {
    try {
      const bonusBurn =
        stateKey !== 'burn' &&
        bonusData?.find((b: BonusListItem | null) => b?.state === 'active');
      if (bonusBurn) {
        burnOpenClickHandler(bonusBurn);
        return;
      }

      if (isDepositBonus(bonus)) {
        setUser({
          ...user,
          isBonusDepositReplenish: true,
          userDepositBonusConditionActive: conditionActive,
          userDepositBonus: bonus,
        });
        setCurrentBonus(null);
        return;
      }

      const bonusIdToActivate = bonus?.id;

      const result = await activate({
        id: bonusIdToActivate,
        userBonusId: bonus?.userBonusId,
      });

      if (result === false) {
        return false;
      }

      if (brandIsPh && bonus?.games?.length === 1) {
        const [bonusGame] = await getGamesByIds(locale, bonus.games).catch(
          () => []
        );

        if (bonusGame) {
          setCurrentBonus(null);
          onBonusBurnModalClose();
          router.push(formatUrl(getGamePlayPath(bonusGame)));
          return;
        }
      }

      onBonusBurnModalClose();

      const updatedData = await mutateBonuses();

      if (updatedData) {
        const updatedBonus = updatedData.find(
          (b: BonusListItem | null) => b?.id === bonusIdToActivate
        );

        if (updatedBonus) {
          setCurrentBonus(updatedBonus);
        }
      }

      scrollToTop();
    } catch (error) {
      console.error('Ошибка в функции onActivate:', error);
      return false;
    }
  };

  const onDeactivate = async (bonus: BonusListItem) => {
    await deactivate({ id: bonus?.id, userBonusId: bonus?.userBonusId });
    onBonusBurnModalClose();
    await mutateBonuses();
  };

  const updateDate = async () => {
    await mutateBonuses();
  };

  const onFilterClick = (filter: string, index: number) => {
    if (index === 0) {
      setSelectedTag(null);
    } else {
      setSelectedTag(filter);
    }
  };

  if (isLoading) {
    return <BonusSkeleton />;
  }

  return (
    <BonusWrapper>
      <PageHeaderSection
        dataWelcomeAvailable={dataWelcomeAvailable}
        isProfileBonusesPage={isProfileBonusesPage}
      />

      <BonusMainContent
        isProfileBonusesPage={isProfileBonusesPage}
        dataWelcomeAvailable={dataWelcomeAvailable}
        visibleFilters={visibleFilters}
        selectedTag={selectedTag}
        onFilterClick={onFilterClick}
        data={data}
        updateDate={updateDate}
        onBonusClick={setCurrentBonus}
      />

      {/* Модалка просмотра бонуса */}
      <BonusViewModal
        bonus={currentBonus}
        onClose={() => setCurrentBonus(null)}
        onActivate={onActivate}
        burnOpenClickHandler={burnOpenClickHandler}
        updateDate={updateDate}
      />

      {/* Модалка сжигания бонуса */}
      <BonusBurnModal
        visible={bonusBurnModalVisible}
        onClose={onBonusBurnModalClose}
        bonus={currentBonusBurn}
        progressWager={progressWager}
        onDeactivate={onDeactivate}
      />
      <MultiAccountWarningModal />
    </BonusWrapper>
  );
};

export { Bonuses };
